import { z } from "zod";
import { decodeFunctionData, type Hex } from "viem";
import { addressSchema } from "../../../api/schemas";
import { ELYSIUM } from "../../../config/elysium";
import { ponsRewardNftABI } from "../../../abis/pons-reward-nft";
import { currentReferralRange, referralRewardRange } from "../contracts/referral-policy";

export const referralMilestoneSchema = z.union([z.literal(50), z.literal(100), z.literal(500)]);
export type ReferralMilestone = z.infer<typeof referralMilestoneSchema>;

const referralCountSchema = z.number().int().nonnegative().safe();
const unixSecondsSchema = z.number().int().positive().safe().max(8_640_000_000_000);
const qualificationFields = {
  actualReferralCount: referralCountSchema.optional(),
  qualificationSource: z.string().min(1).optional(),
  testOverride: z.object({ count: referralCountSchema, expiresAt: unixSecondsSchema }).nullable().optional(),
};
export type ReferralQualification = {
  referralCount: number; actualReferralCount?: number | undefined; qualificationSource?: string | undefined;
  testOverride?: { count: number; expiresAt: number } | null | undefined;
};
function qualificationMatches(value: ReferralQualification): boolean {
  if (value.actualReferralCount !== undefined && value.actualReferralCount > value.referralCount) return false;
  return value.qualificationSource !== "TEST_WALLET_OVERRIDE" ||
    value.actualReferralCount !== undefined && value.testOverride != null && value.testOverride.count <= value.referralCount;
}
const rewardByMilestone = {
  50: { min: 10, max: 50 },
  100: { min: 50, max: 100 },
  500: { min: 100, max: 300 },
} as const;
const rewardBpsSchema = z.object({ min: z.number().int(), max: z.number().int() });
const milestoneStatusSchema = z.object({
  count: referralMilestoneSchema,
  rewardBps: rewardBpsSchema,
  eligible: z.boolean(),
});

function matchesReward(milestone: ReferralMilestone, rewardBps: { min: number; max: number }): boolean {
  const expected = rewardByMilestone[milestone];
  return rewardBps.min === expected.min && rewardBps.max === expected.max;
}

const referralStatusBaseSchema = z.object({
  ...qualificationFields,
  wallet: addressSchema,
  referrer: addressSchema.nullable(),
  referralCount: referralCountSchema,
  milestones: z.array(milestoneStatusSchema).length(3)
    .refine(milestones => new Set(milestones.map(value => value.count)).size === 3, "Duplicate referral milestone"),
}).refine(qualificationMatches, "Referral qualification metadata mismatch")
  .refine(value => value.milestones.every(milestone => milestone.eligible === (value.referralCount >= milestone.count)),
  "Referral eligibility does not match the referral count")
  .refine(value => value.referrer?.toLowerCase() !== value.wallet.toLowerCase(), "A wallet cannot refer itself");

export function referralStatusSchemaForChain(chainId: number | null) {
  return referralStatusBaseSchema.refine(value => (chainId === null ? [4663, ELYSIUM.chainId] : [chainId]).some(chain =>
    value.milestones.every(item => {
      const range = currentReferralRange(item.count, chain);
      return item.rewardBps.min === range.min && item.rewardBps.max === range.max;
    })), "Referral milestone reward mismatch");
}
export const referralStatusSchema = referralStatusSchemaForChain(4663);

// This count belongs to the referrer, not the wallet authenticated by the request.
export const referralBindSchema = z.object({
  referrer: addressSchema,
  referralCount: referralCountSchema,
});

export const referralAuthorizationSchema = z.object({
  ...qualificationFields,
  milestone: referralMilestoneSchema,
  referralCount: referralCountSchema,
  contractAddress: addressSchema,
  chainId: z.number().int().positive().safe(),
  mintFeeWei: z.literal("0"),
  valueWei: z.literal("0"),
  tierUsd: z.enum(["50", "100", "500"]),
  rewardBps: rewardBpsSchema,
  deadline: z.number().int().positive().safe().max(8_640_000_000_000),
  call: z.object({
    to: addressSchema,
    valueWei: z.literal("0"),
    data: z.string().regex(/^0x(?:[0-9a-fA-F]{2}){4,}$/).transform(value => value as Hex),
  }),
}).refine(qualificationMatches, "Referral qualification metadata mismatch")
  .refine(value => value.referralCount >= value.milestone, "Insufficient referrals for authorization")
  .refine(value => value.tierUsd === String(value.milestone), "Referral tier does not match the milestone")
  .refine(value => {
    if (value.chainId !== ELYSIUM.chainId) return matchesReward(value.milestone, value.rewardBps);
    try {
      const call = decodeFunctionData({ abi: ponsRewardNftABI, data: value.call.data });
      if (call.functionName !== "mint") return false;
      const auth = call.args[0];
      const range = referralRewardRange(value.milestone, auth.rewardPolicyVersion, auth.rewardPolicyHash, value.chainId);
      return range !== null && value.rewardBps.min === range.min && value.rewardBps.max === range.max;
    } catch { return false; }
  }, "Referral authorization reward/policy mismatch")
  .refine(value => value.call.to.toLowerCase() === value.contractAddress.toLowerCase(), "Referral mint target mismatch");

export type ReferralStatus = z.infer<typeof referralStatusSchema>;
export type ReferralAuthorization = z.infer<typeof referralAuthorizationSchema>;
